import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { MODAL_WIDTHS } from '@hookdeck/theme';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Text from '../../../../common/base/Text';
import TextAreaInput from '../../../../common/Form/Fields/TextAreaInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { ChangelogEntry } from './useChangelog';
import { formatDate } from '../../../../../utils/date';
import { useDatePreferences } from '../../../../hooks/useDatePreferences';
const StyledPicture = styled.picture`
  display: block;
  width: 100%;
`;
const StyledImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;
const ImagePlaceholder = styled.div<{
  aspect_ratio: number;
  is_loaded: boolean;
}>(({
  theme,
  aspect_ratio,
  is_loaded
}) => css`
    position: relative;
    width: 100%;
    padding-bottom: ${1 / aspect_ratio * 100}%;
    background-color: ${theme.colors.surface.base.variant_surface_3};
    margin-top: 16px;
    margin-bottom: 16px;
    border-radius: ${theme.radius.normal};
    overflow: hidden;
    border: 1px solid ${theme.colors.outline.neutral};

    ${StyledPicture} {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    ${is_loaded && css`
      background-color: transparent;
    `}
  `);
const StyledBackground = styled.div<{
  z_index?: number;
}>`
  z-index: ${({
  theme,
  z_index
}) => z_index ? z_index - 1 : theme.zindex.dropdown - 1};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({
  theme
}) => theme.colors.surface.base.backdrop};
`;
const StyledChangelogFeedCard = styled(StyledCard)`
  overflow-y: auto;
  width: ${MODAL_WIDTHS.small}px;
  position: fixed;
  max-height: calc(100vh - ${({
  theme
}) => theme.spacing(4 * 2)});
  bottom: ${({
  theme
}) => theme.spacing(4)};
  left: 239px;
  display: flex;
  flex-direction: column;
  z-index: ${({
  theme
}) => theme.zindex.dropdown};
`;
const StyledHtmlContent = styled.div`
  * {
    color: ${({
  theme
}) => theme.colors.on.neutral.tertiary_neutral};
  }

  ul {
    padding-left: ${({
  theme
}) => theme.spacing(6)};
  }

  strong {
    font-weight: 600;
  }

  p {
    margin-bottom: ${({
  theme
}) => theme.spacing(4)};
    margin-top: ${({
  theme
}) => theme.spacing(4)};
    line-height: ${20 / 14};
  }

  li {
    p {
      margin-top: ${({
  theme
}) => theme.spacing(2)};
      margin-bottom: ${({
  theme
}) => theme.spacing(2)};
    }
  }

  img {
    max-width: 100%;
  }
`;
const ChangelogItem = t0 => {
  const $ = _c(45);
  const {
    item,
    is_new
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const [is_image_loaded, setIsImageLoaded] = useState(false);
  const [show_feedback, setShowFeedback] = useState(false);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] !== HookdeckAPI.session || $[1] !== HookdeckAPI.track || $[2] !== item.id || $[3] !== item.title) {
    t1 = async () => {
      await HookdeckAPI.track.event("Clicked Changelog Item", {
        item_id: item.id,
        item_title: item.title
      });
      await HookdeckAPI.session.updateMe({
        last_seen_changelog: new Date().toISOString()
      });
    };
    $[0] = HookdeckAPI.session;
    $[1] = HookdeckAPI.track;
    $[2] = item.id;
    $[3] = item.title;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleItemClick = t1;
  let t2;
  if ($[5] !== HookdeckAPI.track || $[6] !== item.title) {
    t2 = async values => {
      if (!values.feedback.trim()) {
        return;
      }
      ;
      try {
        await HookdeckAPI.track.event("Changelog Feedback Submitted", {
          item_title: item.title,
          feedback: values.feedback.trim()
        });
      } catch (t3) {
        const error = t3;
        console.error("Failed to submit feedback:", error);
      }
    };
    $[5] = HookdeckAPI.track;
    $[6] = item.title;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleSubmitFeedback = t2;
  let t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 0
    };
    t4 = {
      align: "center",
      justify: "space-between",
      gap: 4
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "center"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== item.title) {
    t6 = <Text bold={true} flex={t5}>{item.title}</Text>;
    $[11] = item.title;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== is_new) {
    t7 = is_new && <Badge subtle={true} primary={true}>NEW</Badge>;
    $[13] = is_new;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <Text m={t3} flex={t4}>{t6}{t7}</Text>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== item.published_at || $[19] !== timezone) {
    t9 = formatDate(item.published_at, "MMMM d, yyyy", timezone);
    $[18] = item.published_at;
    $[19] = timezone;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <Text muted={true} size="xs">{t9}</Text>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== is_image_loaded || $[24] !== item.image_dimensions || $[25] !== item.image_src_set || $[26] !== item.title) {
    t11 = item.image_src_set && item.image_dimensions && <ImagePlaceholder aspect_ratio={item.image_dimensions.aspect_ratio} is_loaded={is_image_loaded}><StyledPicture><source srcSet={`
              ${item.image_src_set["1x"]} 1x,
              ${item.image_src_set["2x"] || item.image_src_set["1x"]} 2x,
              ${item.image_src_set["3x"] || item.image_src_set["2x"] || item.image_src_set["1x"]} 3x
            `.trim()} /><StyledImage src={item.image_src_set.default || item.image_src_set["2x"] || item.image_src_set["1x"]} alt={item.title} loading="lazy" onLoad={() => setIsImageLoaded(true)} /></StyledPicture></ImagePlaceholder>;
    $[23] = is_image_loaded;
    $[24] = item.image_dimensions;
    $[25] = item.image_src_set;
    $[26] = item.title;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== item.content_html) {
    t12 = <StyledHtmlContent dangerouslySetInnerHTML={{
      __html: item.content_html
    }} />;
    $[28] = item.content_html;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== handleItemClick || $[31] !== item.cta_link || $[32] !== item.cta_text) {
    t13 = item.cta_link && item.cta_text && <Button block={true} outline={true} large={true} as={item.cta_link.startsWith("http") ? "a" : undefined} href={item.cta_link} target="_blank" rel="noopener noreferrer" onClick={handleItemClick} m={{
      t: 2
    }}>{item.cta_text}↗</Button>;
    $[30] = handleItemClick;
    $[31] = item.cta_link;
    $[32] = item.cta_text;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      t: 4
    };
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== handleSubmitFeedback || $[36] !== show_feedback) {
    t15 = <Div m={t14}>{!show_feedback ? <Link neutral={true} onClick={() => setShowFeedback(true)} flex={{
        align: "center",
        justify: "center"
      }} w={100}>Share feedback</Link> : <Formik initialValues={{
        feedback: ""
      }} onSubmit={handleSubmitFeedback}>{t16 => {
          const {
            isSubmitting,
            isValid,
            submitCount
          } = t16;
          return <Form>{submitCount > 0 ? <Text center={true} success={true} size="s"><Icon icon="success" left={true} />Thanks for your feedback!</Text> : <><TextAreaInput name="feedback" placeholder="Your feedback helps us improve..." rows={3} disabled={isSubmitting} required={true} /><Div flex={{
                align: "center",
                justify: "space-between"
              }} m={{
                t: 3
              }}><Button ghost={true} small={true} muted={true} onClick={() => {
                  setShowFeedback(false);
                }} disabled={isSubmitting}>Cancel</Button><Button primary={true} ghost={true} submit={true} disabled={isSubmitting || !isValid}>Send Feedback</Button></Div></>}</Form>;
        }}</Formik>}</Div>;
    $[35] = handleSubmitFeedback;
    $[36] = show_feedback;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t15 || $[43] !== t8) {
    t16 = <StyledCard p={4}>{t8}{t10}{t11}{t12}{t13}{t15}</StyledCard>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t15;
    $[43] = t8;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
};
const ChangelogFeed = t0 => {
  const $ = _c(24);
  const {
    changelog_entries,
    last_seen_changelog,
    onClose
  } = t0;
  let t1;
  if ($[0] !== last_seen_changelog) {
    t1 = entry => {
      if (!last_seen_changelog) {
        return true;
      }
      const last_seen_date = new Date(last_seen_changelog);
      const entry_date = new Date(entry.published_at);
      return entry_date > last_seen_date;
    };
    $[0] = last_seen_changelog;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isNewEntry = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      justify: "space-between"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Text semi={true} m={{
      l: 2
    }}>Changelog</Text>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClose) {
    t4 = <StyledCardSection p={2} flex={t2}>{t3}<Button ghost={true} icon="close" onClick={onClose} /></StyledCardSection>;
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      direction: "column",
      gap: 2,
      grow: true
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== changelog_entries || $[8] !== isNewEntry) {
    let t7;
    if ($[10] !== isNewEntry) {
      t7 = item => <ChangelogItem key={item.id} item={item} is_new={isNewEntry(item)} />;
      $[10] = isNewEntry;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t6 = changelog_entries.map(t7);
    $[7] = changelog_entries;
    $[8] = isNewEntry;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      y: 4
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Text muted={true} center={true} m={t7}>To keep things concise,<br /> only recent updates are displayed.</Text>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6) {
    t9 = <StyledCardSection muted={true} scroll={true} p={2} flex={t5}>{t6}{t8}</StyledCardSection>;
    $[14] = t6;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t9) {
    t10 = <StyledChangelogFeedCard raised={true}>{t4}{t9}</StyledChangelogFeedCard>;
    $[16] = t4;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== onClose) {
    t11 = <StyledBackground onClick={e => {
      e.preventDefault();
      onClose();
    }} />;
    $[19] = onClose;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default ChangelogFeed;